import React, { useState } from "react";
import PanelShell from "../../shared/PanelShell";
import { Skeleton } from "../../shared/Skeleton";
import { fmtCurrency, telHref } from "../../shared/format";
import { ROW_HOVER_FILL, ROW_LIST_BLEED } from "./rowStyles";

// The daily worklist. It empties (an empty list is the success state — no
// followups needed). items[] — reason, who, what they wanted, why not, value.
//
// THE FETCH IS MODALITY-SCOPED, so this panel renders what it is given.
//
// It used to filter client-side, because fn_get_conversation_followups rejected
// p_modality and returned every channel in one array: Verde's payload was 13
// items, 7 of them voice, and the chat page listed phone callers with `tel:`
// links directly below a detail pane that suppresses tap-to-call precisely
// because chat has no phone numbers. p_modality was added 2026-08-27 and the
// filter came out with it — a stopgap kept after its cause is fixed is a second
// place for the rule to live, and the two drift.
const FOLLOWUP_PAGE_SIZE = 3;

const FollowupsPanel = ({ panelKey, say, followups, loading, modality = "voice", onSelect }) => {
  // Nobody calls a website chat. The fallback names what the person actually is
  // on this page, so an unnamed row does not turn every chat visitor into a
  // caller — which on Verde's chat worklist is every row it renders.
  const unnamed = modality === "chat" ? "Unnamed visitor" : "Unnamed caller";
  const items = Array.isArray(followups?.items) ? followups.items : [];

  // THREE PER PAGE, PAGINATED — the worklist shares a three-column grid row and
  // the tallest member sets the row height, so a long list stretched the row and
  // left its neighbours in empty space. Three keeps the card compact; the rest are
  // one page-turn away, never dropped. `page` is 0-indexed; `safePage` clamps it so
  // a refetch that returns fewer items can't strand the view past the last page.
  const pageCount = Math.max(1, Math.ceil(items.length / FOLLOWUP_PAGE_SIZE));
  const [page, setPage] = useState(0);
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * FOLLOWUP_PAGE_SIZE;
  const shown = items.slice(start, start + FOLLOWUP_PAGE_SIZE);
  const paginated = items.length > FOLLOWUP_PAGE_SIZE;
  return (
    <PanelShell
      panelKey={panelKey}
      label="Follow-ups"
      say={say}
      hasData={items.length > 0}
      loading={loading}
      emptyMessage="No follow-ups needed — caught up."
      className="flex flex-col"
    >
      {loading && !followups ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        // One page (up to three rows) renders at a time; pagination below reaches
        // the rest. A fixed page keeps this card's height bounded so it never
        // stretches the shared grid row its neighbours sit in.
        <ul className={`divide-y divide-gray-200 dark:divide-gray-800 ${ROW_LIST_BLEED}`}>
          {shown.map((it, i) => (
            <li key={it.conversation_id ?? i} className={`py-3 flex flex-col gap-1 ${ROW_HOVER_FILL}`}>
              <div className="flex items-start justify-between gap-2 min-w-0">
                {/* The item carries name / phone / email — never `who`, `person`
                    or `caller`, so every row used to read the literal "Caller".
                    A number is a worse label than a name but a far better one
                    than nothing: it is what you call the person back on. */}
                {/* The follow-ups panel is a call-back worklist, so its identifier
                    is tappable whenever it is a number. */}
                {(() => {
                  const who = it.name || it.phone || it.email || unnamed;
                  // contactable:false renders WITHOUT a call affordance (§E.8): it
                  // is the capture problem, not a callable lead, and a dead tel:
                  // link is worse than none. `contactable` is already computed by
                  // the backend, so this only reads it — never derives it.
                  const href = it.contactable === false
                    ? null
                    : (telHref(who) || (it.name ? telHref(it.phone) : null));
                  return href ? (
                    <a href={href} className="gi-body font-medium inline-flex items-center max-md:min-h-[44px] min-w-0 break-words gi-link transition-colors no-underline">
                      {who}
                    </a>
                  ) : (
                    <span className="gi-body font-medium min-w-0 break-words text-gray-900 dark:text-gray-100">{who}</span>
                  );
                })()}
                {it.estimated_value != null && it.estimated_value !== 0 && (
                  <span className="gi-label shrink-0 text-gray-500 dark:text-gray-400">{fmtCurrency(it.estimated_value)}</span>
                )}
              </div>
              {/* `reason` is now a SHORT CATEGORY TAG — one of "Quoted, no booking",
                  "Callback scheduled", "Left a voicemail" (≤18 chars) — and `summary`
                  carries the prose. So the TAG renders as a small label and the PROSE
                  is the primary, readable line. This is the reverse of the old shape,
                  where `reason` was the sentence and `summary` the muted background;
                  showing an 18-char tag as the headline and demoting the explanation
                  to grey would put the emphasis on the label and hide the meaning.
                  When there is no summary the reason becomes the line, and the
                  duplicate tag is dropped. */}
              {it.reason && it.summary && (
                <span
                  data-testid="followup-reason-tag"
                  className="w-fit inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-0.5 gi-label font-medium text-gray-600 dark:text-gray-300"
                >
                  {it.reason}
                </span>
              )}
              {/* The prose line is also the control: it opens THAT one conversation
                  (§2.2), pushing a level 2 directly. The name is already a `tel:`
                  link in the row above, and nesting a link inside a button is invalid
                  and unreachable by keyboard, so the prose — never the name — is the
                  button. NO LINE-CLAMP (§6): the sentence renders verbatim in a
                  growable container; a one-line clamp at 200% text-resize cut prose
                  that needs five lines. */}
              {(() => {
                const line = it.summary || it.reason;
                if (!line) return null;
                return onSelect ? (
                  <button
                    type="button"
                    data-testid="followup-open"
                    onClick={() => onSelect(it, modality)}
                    className="text-left min-h-[44px] w-full rounded-lg gi-body break-words text-gray-700 dark:text-gray-200 hover:bg-gray-900/5 dark:hover:bg-white/5 transition-colors"
                  >
                    {line}
                  </button>
                ) : (
                  <p className="gi-body break-words text-gray-700 dark:text-gray-200">{line}</p>
                );
              })()}
            </li>
          ))}
        </ul>

      )}
      {paginated && (
        <div data-testid="followups-pagination" className="mt-2 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="followups-prev"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous follow-ups"
            className="inline-flex items-center min-h-[44px] text-sm font-medium gi-link transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            Previous
          </button>
          <span data-testid="followups-page-indicator" className="gi-label text-gray-500 dark:text-gray-400" aria-live="polite">
            {`Page ${safePage + 1} of ${pageCount}`}
          </span>
          <button
            type="button"
            data-testid="followups-next"
            onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
            disabled={safePage >= pageCount - 1}
            aria-label="Next follow-ups"
            className="inline-flex items-center min-h-[44px] text-sm font-medium gi-link transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            Next
          </button>
        </div>
      )}
    </PanelShell>
  );
};

export default FollowupsPanel;
